'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Users,
  Award,
  Sparkles,
  CheckCircle2,
  MessageCircle,
} from 'lucide-react';
import { getAllCourses, Course } from '@/lib/curriculumData';
import { LEVELS } from '@/lib/site';

const features = [
  {
    icon: BookOpen,
    title: '체계적인 커리큘럼',
    desc: '레벨별 맞춤 교육으로 단계적 실력 향상을 보장합니다.',
  },
  {
    icon: Users,
    title: '전문 강사진',
    desc: '경험 많은 원어민 강사와 전문 한국인 강사가 함께합니다.',
  },
  {
    icon: Award,
    title: '검증된 실적',
    desc: '높은 수강 만족도와 우수한 시험 성적 달성률.',
  },
];

const programs = [
  {
    title: '기초 영어',
    en: 'Foundations',
    desc: '영어 기초가 부족한 분들을 위한 기본 과정',
    point: '기초 문법 완성 & 필수 어휘 학습',
    img: '/img/basic_course.png',
    pos: 'center',
  },
  {
    title: '시험 대비반',
    en: 'Test Prep',
    desc: '토익, 토플, 텝스 등 각종 영어 시험 대비',
    point: '실전 모의고사 & 유형별 전략',
    img: '/img/exam_prep.png',
    pos: 'center',
  },
  {
    title: '회화 집중반',
    en: 'Conversation',
    desc: '원어민과 함께하는 실전 영어 회화',
    point: '원어민 1:1 수업 & 발음 교정',
    img: '/img/conversation.png',
    pos: 'center',
  },
  {
    title: '비즈니스 영어',
    en: 'Business',
    desc: '직장인을 위한 실무 영어',
    point: '이메일 & 프레젠테이션 영어',
    img: '/img/hero.png',
    pos: '25% center',
  },
];

const marquee = ['TOEIC', 'TOEFL', 'Conversation', 'Business English', 'Pronunciation', 'Grammar', 'Presentation', 'Writing'];

export default function Home() {
  const [courses, setCourses] = useState<Course[]>([]);

  useEffect(() => {
    setCourses(getAllCourses());
  }, []);

  const countBy = (lv: string) => courses.filter((c) => c.level === lv).length;

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(ellipse_at_30%_40%,black_20%,transparent_70%)]"
          style={{ backgroundImage: 'radial-gradient(rgba(35,70,245,.22) 1.2px, transparent 1.4px)', backgroundSize: '24px 24px' }}
          aria-hidden
        />
        <div className="wrap relative grid items-center gap-12 pb-16 pt-10 sm:pb-24 lg:grid-cols-12 lg:gap-8 lg:pt-16">
          <div className="animate-rise lg:col-span-6">
            <span className="kicker">
              <Sparkles className="h-3.5 w-3.5" />
              무료 레벨 테스트 진행 중
            </span>
            <h1 className="display mt-6">
              영어가 <span className="serif-em">자연스러워지는</span>
              <br />
              가장 확실한 방법,
              <br />
              <span className="text-forest">이든 영어학원</span>
            </h1>
            <p className="mt-8 max-w-md text-lg leading-relaxed text-muted">
              체계적인 커리큘럼과 전문 강사진이 함께합니다. 나에게 맞는 레벨부터 차근차근
              시작하세요.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link href="/enrollment" className="btn-coral">
                수강 신청하기
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link href="/curriculum" className="btn-line">
                커리큘럼 보기
              </Link>
            </div>
            <dl className="mt-12 flex gap-10 border-t border-cream-200 pt-8">
              <div>
                <dt className="text-xs font-semibold text-muted">운영 과정</dt>
                <dd className="mt-1 font-serif font-extrabold text-3xl text-ink">{courses.length || 8}<span className="ml-1 text-base font-sans font-bold text-muted">개</span></dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-muted">레벨</dt>
                <dd className="mt-1 font-serif font-extrabold text-3xl text-ink">3<span className="ml-1 text-base font-sans font-bold text-muted">단계</span></dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-muted">원어민 수업</dt>
                <dd className="mt-1 font-serif font-extrabold text-3xl text-ink">1:1</dd>
              </div>
            </dl>
          </div>

          <div className="relative lg:col-span-6">
            <div className="pointer-events-none absolute -right-6 -top-6 h-40 w-40 rounded-[36px] bg-sun sm:-right-10" aria-hidden />
            <div className="pointer-events-none absolute -bottom-6 left-10 h-28 w-28 rounded-full bg-forest" aria-hidden />
            <div className="relative ml-auto aspect-[4/5] w-full max-w-[520px] overflow-hidden rounded-[40px] border-[6px] border-white bg-cream-200 shadow-[0_40px_80px_-30px_rgba(15,29,92,.45)] sm:aspect-[5/6]">
              <img
                src="/img/hero.png"
                alt="이든 영어학원 라운지"
                className="h-full w-full object-cover"
                style={{ objectPosition: '28% center' }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest-950/40 via-transparent to-transparent" />
            </div>

            <div className="absolute -left-2 bottom-12 w-[230px] rounded-3xl rounded-bl-md bg-white p-5 shadow-[0_24px_60px_-20px_rgba(15,29,92,.4)] sm:left-4 lg:-left-8">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-forest text-white">
                  <MessageCircle className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-bold text-ink">원어민 1:1 회화</p>
                  <p className="text-xs text-muted">발음 교정까지 한 번에</p>
                </div>
              </div>
            </div>

            <div className="absolute right-2 top-8 rounded-2xl rounded-br-md bg-ink px-4 py-3 text-white shadow-lg sm:right-6">
              <p className="font-serif text-sm font-extrabold text-sun">Level test</p>
              <p className="text-sm font-bold">무료 상담 · 레벨 진단</p>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <div className="overflow-hidden border-y border-forest-900 bg-forest py-5 text-white">
        <div className="flex w-max animate-marquee gap-10 whitespace-nowrap">
          {[...marquee, ...marquee].map((w, i) => (
            <span key={i} className="flex items-center gap-10 font-serif font-extrabold text-2xl">
              {w}
              <span className="text-sun">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* Why Eden */}
      <section className="bg-cream py-20 sm:py-28">
        <div className="wrap">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <p className="font-serif font-extrabold text-xl text-forest-600">Why Eden</p>
              <h2 className="title mt-3">
                이든 영어학원의
                <br />
                특별함
              </h2>
              <p className="mt-5 text-muted">
                단순히 가르치는 곳이 아니라, 실제로 말하고 쓰게 되는 곳을 만듭니다.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-3 lg:col-span-8">
              {features.map((f, i) => (
                <div
                  key={f.title}
                  className={`flex flex-col rounded-[28px] p-7 ${
                    i === 1 ? 'bg-forest text-white' : 'border border-cream-200 bg-white'
                  }`}
                >
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl ${
                      i === 1 ? 'bg-sun text-forest' : 'bg-forest-50 text-forest-700'
                    }`}
                  >
                    <f.icon className="h-6 w-6" />
                  </span>
                  <p className={`mt-10 font-serif font-extrabold text-sm ${i === 1 ? 'text-white/50' : 'text-muted'}`}>0{i + 1}</p>
                  <h3 className="mt-1 text-xl font-bold">{f.title}</h3>
                  <p className={`mt-2 text-[15px] leading-relaxed ${i === 1 ? 'text-white/70' : 'text-muted'}`}>{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="font-serif font-extrabold text-xl text-forest-600">Programs</p>
              <h2 className="title mt-3">목표에 맞는 프로그램</h2>
            </div>
            <Link href="/curriculum" className="btn-line self-start sm:self-auto">
              전체 커리큘럼
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {programs.map((p) => (
              <Link
                key={p.title}
                href="/curriculum"
                className="group flex flex-col overflow-hidden rounded-[28px] bg-cream transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-24px_rgba(29,59,47,.4)]"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    style={{ objectPosition: p.pos }}
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 font-serif font-extrabold text-sm text-forest backdrop-blur">
                    {p.en}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="text-xl font-bold text-ink">{p.title}</h3>
                  <p className="mt-2 text-[15px] text-muted">{p.desc}</p>
                  <p className="mt-auto flex items-center gap-2 pt-6 text-sm font-semibold text-forest-700">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    {p.point}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Level path */}
      <section className="bg-cream py-20 sm:py-28">
        <div className="wrap">
          <div className="text-center">
            <p className="font-serif font-extrabold text-xl text-forest-600">Your path</p>
            <h2 className="title mt-3">
              초급부터 고급까지,
              <br className="sm:hidden" /> 한 단계씩
            </h2>
          </div>

          <div className="relative mt-14 grid gap-4 md:grid-cols-3">
            <div className="absolute left-[16%] right-[16%] top-[52px] hidden border-t-2 border-dashed border-forest-100 md:block" aria-hidden />
            {(Object.keys(LEVELS) as (keyof typeof LEVELS)[]).map((lv, i) => {
              const L = LEVELS[lv];
              const names = courses.filter((c) => c.level === lv).map((c) => c.name);
              return (
                <Link
                  key={lv}
                  href={`/curriculum?level=${lv}`}
                  className="group relative flex flex-col items-center rounded-[28px] border border-cream-200 bg-white px-6 pb-8 pt-8 text-center transition-colors hover:border-forest"
                >
                  <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-forest font-serif font-extrabold text-xl text-white ring-8 ring-white">
                    {i + 1}
                  </span>
                  <p className="mt-5 font-serif font-extrabold text-lg text-muted">{L.en}</p>
                  <h3 className="text-2xl font-bold text-ink">{L.name}</h3>
                  <p className="mt-1 text-sm font-semibold text-forest-600">{countBy(lv)}개 과정</p>
                  <ul className="mt-5 flex flex-wrap justify-center gap-1.5">
                    {names.map((n) => (
                      <li key={n} className="rounded-full bg-cream px-3 py-1 text-xs font-semibold text-muted">
                        {n}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-6 flex items-center gap-1 text-sm font-bold text-forest group-hover:text-forest-600">
                    과정 보기 <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cream pb-20 sm:pb-28">
        <div className="wrap">
          <div className="relative overflow-hidden rounded-[36px] bg-forest px-7 py-14 text-white sm:px-14 sm:py-20">
            <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-sun/25 blur-3xl" aria-hidden />
            <div className="pointer-events-none absolute -bottom-24 left-1/3 h-72 w-72 rounded-full bg-sun/30 blur-3xl" aria-hidden />
            <div className="relative grid items-center gap-10 lg:grid-cols-2">
              <div>
                <p className="font-serif font-extrabold text-xl text-sun">Start today</p>
                <h2 className="mt-3 text-3xl font-bold leading-tight tracking-tight sm:text-[42px] sm:leading-[1.2]">
                  지금 바로 시작하세요
                </h2>
                <p className="mt-4 max-w-md text-white/70">
                  무료 레벨 테스트와 상담을 통해 나에게 맞는 과정을 찾아보세요.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 lg:justify-end">
                <Link href="/enrollment" className="btn-coral">
                  수강 신청하기
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link href="/contact" className="btn border border-cream/25 text-white hover:bg-cream hover:text-forest">
                  상담 문의
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
